import { create } from 'zustand';

import { api } from './api.js';
import type { RenderCapabilities } from './capabilities.js';
import type { UserDto } from './types.js';

export type AuthStatus = 'unknown' | 'anonymous' | 'authenticated';

/** Overlay state. Gameplay state never lives here; it lives in the shared simulation. */
export interface AppState {
  capabilities: RenderCapabilities | null;
  setCapabilities: (c: RenderCapabilities) => void;
  user: UserDto | null;
  authStatus: AuthStatus;
  setUser: (user: UserDto | null) => void;
  /** Asks the API who the cookie belongs to. */
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useAppStore = create<AppState>((set) => ({
  capabilities: null,
  setCapabilities: (capabilities) => set({ capabilities }),
  user: null,
  authStatus: 'unknown',
  setUser: (user) => set({ user, authStatus: user ? 'authenticated' : 'anonymous' }),
  refreshUser: async () => {
    try {
      const { user } = await api<{ user: UserDto | null }>('/api/v1/auth/me');
      set({ user, authStatus: user ? 'authenticated' : 'anonymous' });
    } catch {
      set({ user: null, authStatus: 'anonymous' });
    }
  },
  logout: async () => {
    try {
      await api('/api/v1/auth/logout', { method: 'POST' });
    } finally {
      set({ user: null, authStatus: 'anonymous' });
    }
  },
}));
